import {
  offset,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import {
  faCalendarDays,
  faEnvelope,
  faLink,
  faMessage,
  faPhone
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { CallAction } from '~/components/Scopes/Actions/CallAction'
import { EmailAction } from '~/components/Scopes/Actions/EmailAction'
import { MeetingAction } from '~/components/Scopes/Actions/MeetingAction'
import { MessageAction } from '~/components/Scopes/Actions/MessageAction'
import type { AsideActionPosition } from '~/types/global'
import { AsideActionContext } from '~/utils/contextes'
import { confirmCloseAction } from '../../Actions/ActionUtils'
import { AddToOrderAction } from '../../Actions/AddToOrderAction'
import {
  actionItem,
  actionItemActive,
  actions,
  emailChoiceContainer,
  emailChoiceOption
} from './ActionsButtonList.css'

const actionNameMap = {
  call: 'Planifier un appel',
  message: 'Envoyer un SMS',
  email: 'Envoyer un mail',
  meeting: 'Planifier un rendez-vous',
  addToOrder: 'Associer à une commande'
}

export const ApplicantActions = ({
  applicantIds,
  contextOrderId,
  removeApplicantIdFromList,
  onActionHover,
  onCurrentActionChange,
  availablePositions,
  defaultPosition
}: {
  applicantIds: number[]
  contextOrderId?: number
  removeApplicantIdFromList?: (applicantId: number) => void
  onActionHover?: (action: string | null) => void
  onCurrentActionChange?: (action: string | null) => void
  availablePositions?: AsideActionPosition[]
  defaultPosition?: AsideActionPosition
}) => {
  const { setAsideAction, isDirty, resetAction, asideAction } =
    useContext(AsideActionContext)
  const [emailChoice, setEmailChoice] = useState(false)

  // Retain the original action list to active only self buttons when the action is triggered
  // In case like kanban with ton of action list, it avoid to have all action list buttons active
  const [originalActionList, setOriginalActionList] = useState(false)
  useEffect(() => {
    if (asideAction === null && originalActionList) {
      setOriginalActionList(false)
    }
  }, [originalActionList, asideAction])

  // Memo the current action based on context to highlight the correct button
  const currentAction = useMemo((): keyof typeof actionNameMap | null => {
    if (
      asideAction &&
      typeof asideAction === 'object' &&
      'type' in asideAction
    ) {
      switch (asideAction.type) {
        case CallAction:
          return 'call'
        case MessageAction:
          return 'message'
        case EmailAction:
          return 'email'
        case MeetingAction:
          return 'meeting'
        case AddToOrderAction:
          return 'addToOrder'
      }
    }
    return null
  }, [asideAction])

  const setAction = useCallback(
    (action: ReactNode, isWide?: boolean, position?: AsideActionPosition) => {
      setAsideAction({
        action,
        position:
          position && availablePositions?.includes(position)
            ? position
            : defaultPosition,
        availablePositions: availablePositions,
        isWide
      })
    },
    [setAsideAction, defaultPosition, availablePositions]
  )

  const closeAsideAction = async () => {
    if (!isDirty) {
      resetAction()
      return true
    }
    const confirm = await confirmCloseAction(true)
    if (confirm) {
      resetAction()
      return true
    }
    return false
  }

  const handleClick =
    (callback: () => void) => async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      const confirm = await closeAsideAction()
      if (confirm) {
        setOriginalActionList(true)
        callback()
      }
    }

  useEffect(() => {
    onCurrentActionChange?.(currentAction ? actionNameMap[currentAction] : null)
  }, [currentAction, onCurrentActionChange])

  const {
    refs: emailChoiceRefs,
    floatingStyles: emailChoiceStyles,
    context: contextEmailChoice
  } = useFloating({
    open: emailChoice,
    onOpenChange: setEmailChoice,
    placement: 'bottom-end',
    middleware: [offset(10)]
  })

  const adaptablePosition = availablePositions?.includes('modal')
    ? 'modal'
    : availablePositions?.includes('sidebar')
      ? 'sidebar'
      : 'bottom'

  const dismiss = useDismiss(contextEmailChoice)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  return (
    <div className={actions}>
      <ActionItem
        nameKey="call"
        icon={faPhone}
        onClick={handleClick(() => {
          setAction(<CallAction applicantIds={applicantIds} />)
        })}
        active={currentAction === 'call' && originalActionList}
        onActionHover={onActionHover}
      />
      <ActionItem
        nameKey="message"
        icon={faMessage}
        onClick={handleClick(() => {
          setAction(
            <MessageAction
              applicantIds={applicantIds}
              removeApplicantIdFromList={removeApplicantIdFromList}
            />
          )
        })}
        active={currentAction === 'message' && originalActionList}
        onActionHover={onActionHover}
      />
      <ActionItem
        nameKey="email"
        icon={faEnvelope}
        onClick={(e) => {
          e.stopPropagation()
          if (contextOrderId) {
            setEmailChoice(!emailChoice)
          } else {
            handleClick(() => {
              setAction(
                <EmailAction
                  applicantIds={applicantIds}
                  sendTo="applicant"
                  orderId={contextOrderId}
                  removeApplicantIdFromList={removeApplicantIdFromList}
                />,
                true,
                adaptablePosition
              )
            })(e)
          }
        }}
        active={
          (currentAction === 'email' && originalActionList) || emailChoice
        }
        ref={emailChoiceRefs.setReference}
        onActionHover={onActionHover}
        {...getReferenceProps()}
      />
      {emailChoice &&
        createPortal(
          <div
            className={emailChoiceContainer}
            ref={emailChoiceRefs.setFloating}
            style={emailChoiceStyles}
            {...getFloatingProps()}
          >
            <div
              className={emailChoiceOption}
              onClick={handleClick(() => {
                setEmailChoice(false)
                setAction(
                  <EmailAction
                    applicantIds={applicantIds}
                    sendTo="applicant"
                    orderId={contextOrderId}
                    removeApplicantIdFromList={removeApplicantIdFromList}
                  />,
                  true,
                  adaptablePosition
                )
              })}
            >
              Envoyer un email{' '}
              {applicantIds.length === 1 ? 'au candidat' : 'aux candidats'}{' '}
            </div>
            <div
              className={emailChoiceOption}
              onClick={handleClick(() => {
                setEmailChoice(false)
                setAction(
                  <EmailAction
                    applicantIds={applicantIds}
                    sendTo="contact"
                    orderId={contextOrderId}
                  />,
                  true,
                  adaptablePosition
                )
              })}
            >
              Envoyer par email au contact
            </div>
          </div>,
          document.querySelector('#appWrapper') as HTMLElement
        )}
      <ActionItem
        nameKey="meeting"
        icon={faCalendarDays}
        onClick={handleClick(() => {
          setAction(
            <MeetingAction
              applicantIds={applicantIds}
              orderId={contextOrderId}
            />
          )
        })}
        active={currentAction === 'meeting' && originalActionList}
        onActionHover={onActionHover}
      />
      {!contextOrderId && (
        <ActionItem
          nameKey="addToOrder"
          icon={faLink}
          onClick={handleClick(() => {
            setAction(<AddToOrderAction applicantIds={applicantIds} />)
          })}
          active={currentAction === 'addToOrder' && originalActionList}
          onActionHover={onActionHover}
        />
      )}
    </div>
  )
}

const ActionItem = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & {
    nameKey: keyof typeof actionNameMap
    icon: any
    title?: string
    active?: boolean
    onActionHover?: (action: string | null) => void
  }
>(({ icon, active, nameKey, title, onActionHover, ...rest }, ref) => (
  <div
    className={clsx(actionItem, active ? actionItemActive : null)}
    ref={ref}
    title={nameKey ? actionNameMap[nameKey] : title}
    onMouseEnter={() =>
      onActionHover?.(nameKey ? actionNameMap[nameKey] : null)
    }
    onMouseLeave={() => onActionHover?.(null)}
    {...rest}
  >
    <FontAwesomeIcon icon={icon} />
  </div>
))
